import type { JSX } from 'react';
import { memo, useContext } from 'react';

import useIntakeProgress, { IntakeStage } from '@/hooks/useIntakeProgress';
import useSSIEligibilityDetails from '@/hooks/useSSIEligibilityDetails';
import { InsuranceType } from '@/pages/members/constants/insurance';
import PersonContext from '@/pages/Person/PersonContext';
import CompleteChecklistItem from '@/pages/Profile/components/ChecklistSection/components/CompleteChecklistItem';
import IncompleteChecklistItem from '@/pages/Profile/components/ChecklistSection/components/IncompleteChecklistItem';
import IntakeStatusBadge from '@/pages/Profile/components/ChecklistSection/components/IntakeStatusBadge';
import getDefaultInsurancePolicy from '@/pages/Profile/components/ChecklistSection/utils/getDefaultInsurancePolicy';
import insurancePolicyFieldsForCompletion from '@/pages/Profile/components/ChecklistSection/utils/insurancePolicyFieldsForCompletion';
import { SECURE_SHIPPING_ADDRESS_SLUG } from '@/types/address';
import Heading from '@/ui/components/Heading';
import Tooltip from '@/ui/components/Tooltip';
import { daysBetween, formatISOStringToReadableDate, isToday, isYesterday } from '@/utils/dates';
import { healthieLink } from '@/utils/healthieLinks';

import isEligibleForSelfScheduledIntake from '../../utils/checkSSIEligibility';
import getIndexForDefault from '../../utils/getIndexForDefault';
import PSI_STEPS from '../../utils/psiSteps';

import { ChecklistItem, IntakeChecklistProps } from './types';

const IntakeChecklist = ({ className }: IntakeChecklistProps): JSX.Element => {
  const { person } = useContext(PersonContext);
  const { currentStage, eligibilityReason, lastCompletedItem, lastActivityDate, nextItem } =
    useIntakeProgress(person);
  const isSSIIneligible = currentStage === IntakeStage.NOT_SSI_ELIGIBLE;
  const { data: ssiEligibilityData } = useSSIEligibilityDetails(person?.id || '', isSSIIneligible);

  const defaultInsurancePolicy = getDefaultInsurancePolicy(person.insurance_policies);

  const host = window.location.origin;

  const hasClassification = (classificationKey: string): boolean => {
    return person.classifications.includes(classificationKey);
  };

  const isMentalHealth = hasClassification('drug_category.mental_health');

  const personalInfoComplete = () => {
    const emailCompleted = !!person.default_email;
    const phoneCompleted = !!person.default_phone;
    const sexInfoCompleted = !!person.sex;

    const addressCompleted = (): boolean => {
      const defaultIndex = getIndexForDefault(person, 'addresses');
      const defaultAddress = person.addresses[defaultIndex];

      const baseAddressFields = [
        !!defaultAddress?.address1,
        !!defaultAddress?.city,
        !!defaultAddress?.state,
        !!defaultAddress?.country,
        !!defaultAddress?.postal_code
      ];

      if (isMentalHealth) {
        return baseAddressFields.every((field) => field);
      }

      return [
        ...baseAddressFields,
        defaultAddress?.address_type?.slug === SECURE_SHIPPING_ADDRESS_SLUG
      ].every((field) => field);
    };
    return emailCompleted && phoneCompleted && addressCompleted() && sexInfoCompleted;
  };

  const insuranceCardsUploaded = () => {
    // If the person has Medicaid insurance, we consider the cards uploaded
    if (
      defaultInsurancePolicy?.type?.toLowerCase().includes(InsuranceType.MEDICAID.toLowerCase())
    ) {
      return true;
    }

    return defaultInsurancePolicy
      ? !!defaultInsurancePolicy.insurance_card_images?.back_image_url &&
          !!defaultInsurancePolicy.insurance_card_images?.front_image_url
      : false;
  };

  const insuranceInfoComplete = () => {
    const infoCompleted = defaultInsurancePolicy
      ? insurancePolicyFieldsForCompletion
          .map((field) => {
            return !!defaultInsurancePolicy[field];
          })
          .every((field) => field)
      : false;

    return infoCompleted;
  };

  const checklistItems: ChecklistItem[] = [
    {
      id: 'tc_assigned',
      title: 'TC assigned',
      completed:
        person.staff_relationships.filter(
          (sr) => sr.relationship_type?.slug === 'treatment-consultant'
        ).length > 0,
      tooltip: 'This person has a treatment consultant assigned',
      shouldShow: true,
      link: '#care-team'
    },
    {
      id: 'first_app_login',
      title: 'First App Login',
      completed: !!person.last_app_open_at,
      tooltip: 'Person has logged into the mobile app',
      shouldShow: true,
      link: `/people/${person.id}/timeline?action=App+Open`
    },
    {
      id: 'personal_information',
      title: 'Profile Information Complete',
      completed: personalInfoComplete(),
      tooltip: isMentalHealth
        ? 'Email, phone number, address, and sex are filled out'
        : 'Secure shipping address, email, phone number, and sex are filled out',
      shouldShow: true,
      link: '#basic-info-section'
    },
    {
      id: 'discover_program_challenges',
      title: 'Discover Our Program Challenge',
      completed: hasClassification('self_scheduling_intake.discover_program_challenges'),
      tooltip: 'Person has completed the required "Discover Our Program" challenge',
      shouldShow: isEligibleForSelfScheduledIntake(person),
      link: `/people/${person.id}/timeline?search=Discover+Our+Program`
    },
    {
      id: 'payment_method_requirements_met',
      title: 'Payment Method Set',
      completed: hasClassification('self_scheduling_intake.payment_method_requirements_met'),
      tooltip: 'A valid payment method is on file',
      shouldShow: isEligibleForSelfScheduledIntake(person),
      link: `${healthieLink({ host })}/users/${person.healthie_id}`
    },
    {
      id: 'insurance_cards_uploaded',
      title: 'Insurance Cards Uploaded',
      completed: insuranceCardsUploaded(),
      tooltip:
        'Person has uploaded front and back images of their insurance card or has Medicaid insurance',
      shouldShow: true,
      link: '#insurance'
    },
    {
      id: 'insurance_information_complete',
      title: 'Insurance Information Provided',
      completed: insuranceInfoComplete(),
      tooltip:
        'The following insurance information is complete: Insurance Carrier, Plan Name, Type, Subscriber ID, Priority Type, Relationship, and Last Verification',
      shouldShow: true,
      link: '#insurance'
    },
    {
      id: 'insurance_verified',
      title: 'Insurance Verified',
      completed: hasClassification('insurance.active_verified'),
      tooltip: 'Insurance has been verified and is active',
      shouldShow: true,
      link: '#insurance'
    },
    ...PSI_STEPS.map((step): ChecklistItem => ({
      id: step.id,
      title: step.title,
      completed: hasClassification(step.classificationKey),
      tooltip: step.tooltip,
      shouldShow: step.shouldShow(person),
      link: step.link(person.id)
    }))
  ];

  const formatRelativeDate = (dateString: string | null) => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      const timezone = person?.time_zone || 'UTC';

      const timeString = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });

      if (isToday(date, timezone)) {
        return `🔥 Today at ${timeString}`;
      } else if (isYesterday(date, timezone)) {
        return `🔥 Yesterday at ${timeString}`;
      } else {
        const daysAgo = parseInt(daysBetween(date, new Date()), 10);
        return `🧊 ${daysAgo} days ago`;
      }
    } catch {
      return formatISOStringToReadableDate(dateString);
    }
  };

  return (
    <div className={className}>
      <div className="flex items-center gap-2 mb-2">
        <Heading text="Intake Checklist" value={3} />
        <IntakeStatusBadge
          stage={currentStage}
          eligibilityReason={eligibilityReason}
          ineligibilityReasons={ssiEligibilityData?.data?.ineligibility_reasons}
        />
      </div>
      <div>
        {checklistItems
          .filter((item) => item.shouldShow)
          .map((item) => {
            const Component = item.completed ? CompleteChecklistItem : IncompleteChecklistItem;
            return (
              <div key={item.id} className="flex items-center">
                <Tooltip tooltipContent={item.tooltip} className="ml-2">
                  <Component text={item.title} link={item.link} />
                </Tooltip>
              </div>
            );
          })}
      </div>

      {currentStage !== IntakeStage.NOT_SSI_ELIGIBLE && (
        <div className="mt-4">
          {lastActivityDate && (
            <div className="mb-3">
              <Heading value={5} text="Last Intake Activity:" />
              <span className="text-sm">{formatRelativeDate(lastActivityDate)}</span>
            </div>
          )}

          {lastCompletedItem && (
            <div className="mb-3">
              <Heading value={5} text="Last Completed Step:" />
              <CompleteChecklistItem text={lastCompletedItem.title} />
            </div>
          )}

          {nextItem && (
            <div className="mb-3">
              <Heading value={5} text="Next Step:" />
              <IncompleteChecklistItem text={nextItem.title} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default memo(IntakeChecklist);
